import {
  AUDIO_ATTACK_S,
  AUDIO_MIN_ATTACK_S,
  AUDIO_DCBLOCK_HZ,
  SAMPLE_ONSET_FADE_S,
  AUDIO_BASE_MIDI,
  AUDIO_BELL_LEVEL,
  CHOKE_FADE_S,
  AUDIO_BELL_MOD_INDEX,
  AUDIO_BELL_MOD_RATIO,
  AUDIO_BELL_TRANSPOSE,
  AUDIO_COOLDOWN_MS,
  AUDIO_COUNT_EXP,
  AUDIO_DECAY_S,
  AUDIO_DENSITY_ATTACK_ADD_S,
  AUDIO_DENSITY_DECAY_MUL,
  AUDIO_DENSITY_DETUNE_CENTS,
  AUDIO_DENSITY_DUCK_FLOOR,
  AUDIO_BLUR_FADE_TAU,
  AUDIO_DENSITY_DUCK_TAU,
  AUDIO_DENSITY_GAIN_FLOOR,
  AUDIO_DENSITY_RATE_HIGH,
  AUDIO_DENSITY_RATE_LOW,
  AUDIO_DENSITY_TAU,
  AUDIO_DENSITY_TRANSIENT,
  AUDIO_GAIN_MAX,
  AUDIO_GAIN_PER_HIT,
  AUDIO_LOOKAHEAD_S,
  AUDIO_ONSET_LATENCY_S,
  AUDIO_ONSET_ANCHOR_FOLLOW,
  AUDIO_ONSET_LOCK,
  AUDIO_PREWARM_GAIN,
  AUDIO_PREWARM_VOICES,
  AUDIO_MASTER_GAIN,
  IMPACT_MAX,
  IMPACT_REF,
  LIMITER_ATTACK_S,
  LIMITER_KNEE_DB,
  LIMITER_RATIO,
  LIMITER_RELEASE_S,
  LIMITER_THRESHOLD_DB,
  MAX_OBJECTS,
  PITCH_INDEX_MAX,
  PITCH_INDEX_MIN,
  RAIL_PAD_DETUNE_CENTS,
  RAIL_VIB_DEPTH_RATIO,
  RAIL_VIB_RATE_HZ,
  RAIL_VOICE_FREQ_TAU,
  RAIL_VOICE_GAIN_MAX,
  RAIL_VOICE_GAIN_PER,
  RAIL_VOICE_GAIN_TAU,
  RAIL_VOICE_LP_HZ,
  RAIL_VOICE_RELEASE_S,
  REVERB_AMOUNT_DEFAULT,
  REVERB_IR_DECAY,
  REVERB_IR_SECONDS,
  REVERB_SEND_MAX,
  REVERB_SEND_TAU,
  SUSTAIN_DAMP_HOLD_S,
  SUSTAIN_DAMP_TAU,
  SUSTAIN_HOLD_THRESHOLD,
  VOICE_CAP,
} from "./constants";
import { PlacedObject } from "./objects";
import { ONSET_DIAG, recordOnsetFire } from "./onsetDiag";
import { track } from "./telemetry";
import synthWorkletUrl from "./synth-worklet.ts?worker&url";
import sampleWorkletUrl from "./sample-worklet.ts?worker&url";

/**
 * A timbre's stable catalog id. Scenes persist this id string, so the catalog can
 * reorder or add entries freely as long as ids keep their meaning.
 */
export type Timbre = string;

export interface SampleZone {
  url: string;
  rootMidi: number;
}

/**
 * Drum kit piece names ordered low-to-high for the piece selection slider.
 * The tom samples follow standard drum numbering (tom1 = highest, tom3 = floor
 * tom; measured ~122/97/93 Hz), so the pitch-ascending order lists tom3 first.
 */
export const KIT_PIECES: readonly string[] = ["kick", "tom3", "tom2", "tom1", "snare", "hat", "cymbal"];

/**
 * Timbres whose sustain pedal a global node exposes (one `sustain <timbre>`
 * param each); extending sustain to another timbre is one entry here plus
 * engine support. Only pitched-sample timbres qualify today: tone/wood decay
 * is the body of those sounds (no audible hold), and bell's scheduled gain
 * ramps would need cancel-and-hold handling rather than the held-voice path.
 */
export const SUSTAIN_TIMBRES: readonly Timbre[] = ["piano"];

/** Returns the zone whose rootMidi is closest to targetMidi (ties prefer earlier in array). */
export function nearestZone(zones: readonly SampleZone[], targetMidi: number): SampleZone {
  return zones.reduce((best: SampleZone, zone: SampleZone): SampleZone =>
    Math.abs(zone.rootMidi - targetMidi) < Math.abs(best.rootMidi - targetMidi) ? zone : best
  );
}

/** Maps a scale degree to a kit piece index using safe modulo for negative degrees. */
export function kitPieceIndex(degree: number, pieceCount: number): number {
  return ((degree % pieceCount) + pieceCount) % pieceCount;
}

/**
 * Waveform of a rail's sustained glissando voice. Separate from `Timbre`
 * (impact recipes): rail voices are continuous oscillator graphs, not enveloped
 * one-shots, so they pick from their own set — tri (soft default), sine
 * (theremin-pure), saw (string/lead through the lowpass), pad (two detuned
 * saws), vib (sine with an LFO vibrato).
 */
export type RailTimbre = "tri" | "sine" | "saw" | "pad" | "vib";

export const RAIL_TIMBRES: readonly RailTimbre[] = ["tri", "sine", "saw", "pad", "vib"];

export interface RailTimbreCatalogEntry {
  id: RailTimbre;
  name: string;
}

/** Append-only rail-timbre catalog: id (the RailTimbre union member) + display name, for the UI and the `timbre` int param. */
export const RAIL_TIMBRE_CATALOG: readonly RailTimbreCatalogEntry[] = [
  { id: "tri", name: "Tri" },
  { id: "sine", name: "Sine" },
  { id: "saw", name: "Saw" },
  { id: "pad", name: "Pad" },
  { id: "vib", name: "Vibrato" },
];

/** Catalog index of a rail timbre id (0 when unknown). */
export function railTimbreIndexById(id: RailTimbre): number {
  const i = RAIL_TIMBRE_CATALOG.findIndex((e: RailTimbreCatalogEntry): boolean => e.id === id);
  return i < 0 ? 0 : i;
}

/** Rail timbre id at a catalog index, clamped into range. */
export function railTimbreIdByIndex(index: number): RailTimbre {
  const clamped = Math.min(Math.max(Math.round(index), 0), RAIL_TIMBRE_CATALOG.length - 1);
  return RAIL_TIMBRE_CATALOG[clamped].id;
}

/** Display name for a rail timbre id (the id itself when unknown). */
export function railTimbreName(id: RailTimbre): string {
  return RAIL_TIMBRE_CATALOG.find((e: RailTimbreCatalogEntry): boolean => e.id === id)?.name ?? id;
}

/** Semitone offsets of the pentatonic set within one octave; the touchable set in simple mode. */
export const PENTATONIC_OFFSETS: readonly number[] = [0, 2, 4, 7, 9];

const NOTE_NAMES: readonly string[] = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const NOTE_NAMES_JA: readonly string[] = ["ド", "ド#", "レ", "レ#", "ミ", "ファ", "ファ#", "ソ", "ソ#", "ラ", "ラ#", "シ"];

/** Absolute MIDI note for a pitch index (semitone offset from AUDIO_BASE_MIDI; 0 = AUDIO_BASE_MIDI). */
export function pitchIndexToMidi(pitchIndex: number): number {
  return AUDIO_BASE_MIDI + pitchIndex;
}

/** Inverse of pitchIndexToMidi. */
export function midiToPitchIndex(midi: number): number {
  return midi - AUDIO_BASE_MIDI;
}

/** Note name for a pitch index. With showOctave=true returns SPN form (e.g. "C3", "F#4"). */
export function noteNameForPitchIndex(pitchIndex: number, showOctave: boolean = false, notation: "en" | "ja" = "en"): string {
  const midi = pitchIndexToMidi(pitchIndex);
  const pitchClass = ((midi % 12) + 12) % 12;
  const octaveNum = Math.floor(midi / 12) - 1;
  const noteName = notation === "ja" ? NOTE_NAMES_JA[pitchClass] : NOTE_NAMES[pitchClass];
  return showOctave ? `${noteName}${octaveNum}` : noteName;
}

/**
 * The pitch index a pitched voice actually sounds / displays at: the stored
 * index plus the scene-wide transpose offset, except kit timbres whose
 * `pitchIndex` selects a kit piece rather than a note, so a transpose must
 * leave it untouched. The offset is runtime modulation — the stored index is
 * never rewritten — so every sounding / coloring / labeling consumer routes
 * its stored index through here to stay in sync.
 */
export function effectivePitchIndex(stored: number, timbreId: Timbre, transpose: number): number {
  return isKitTimbre(timbreId) ? stored : stored + transpose;
}

/**
 * The pitch index one step from `cur` in the given direction within the allowed
 * pitch-class set, across every matching degree in the full
 * [PITCH_INDEX_MIN, PITCH_INDEX_MAX] range and wrapping at the range ends: up
 * advances to the next higher degree (wrapping to the lowest), down to the next
 * lower (wrapping to the highest). The single source of truth for both
 * pentatonic stepping and PitchQuantize-mask stepping. An empty `classes`
 * leaves `cur` unchanged (no degree to move onto).
 */
export function scaleNeighbor(cur: number, dir: 1 | -1, classes: readonly number[]): number {
  const degrees: number[] = [];
  for (let p = PITCH_INDEX_MIN; p <= PITCH_INDEX_MAX; p++) {
    if (classes.includes(((p % 12) + 12) % 12)) {
      degrees.push(p);
    }
  }
  if (degrees.length === 0) {
    return cur;
  }
  if (dir > 0) {
    return degrees.find((p: number): boolean => p > cur) ?? degrees[0];
  }
  return [...degrees].reverse().find((p: number): boolean => p < cur) ?? degrees[degrees.length - 1];
}

/** The pentatonic pitch index one step from `cur`; the simple-mode specialization of `scaleNeighbor`. */
export function pentatonicNeighbor(cur: number, dir: 1 | -1): number {
  return scaleNeighbor(cur, dir, PENTATONIC_OFFSETS);
}

/** Whether a pitch index lands on a pentatonic degree in any octave. */
export function isPentatonic(pitchIndex: number): boolean {
  const within = ((pitchIndex % 12) + 12) % 12;
  return PENTATONIC_OFFSETS.includes(within);
}

/**
 * Snaps a semitone to the nearest pitch index whose class is in the allowed set,
 * across every octave; an exact tie resolves to the lower (smaller) semitone.
 * Candidates are generated for the octave bracketing the input and its
 * neighbours, which always covers the nearest degree since classes span < 12
 * semitones. An empty `classes` returns the input unchanged.
 */
export function snapToScale(semitone: number, classes: readonly number[]): number {
  if (classes.length === 0) {
    return semitone;
  }
  const baseOct = Math.floor(semitone / 12);
  let best = semitone;
  let bestDist = Infinity;
  for (let k = baseOct - 1; k <= baseOct + 1; k++) {
    for (const offset of classes) {
      const cand = k * 12 + offset;
      const dist = Math.abs(cand - semitone);
      if (dist < bestDist) {
        bestDist = dist;
        best = cand;
      }
    }
  }
  return best;
}

/** Snaps a semitone to the nearest pentatonic pitch index; the simple-mode specialization of `snapToScale`. */
export function snapToPentatonic(semitone: number): number {
  return snapToScale(semitone, PENTATONIC_OFFSETS);
}

/**
 * One sounding note. `gain` is the single anchor into master: the AD envelope
 * lives here and voice stealing/cleanup disconnect from here, so the steal path
 * stays identical regardless of how many oscillators a timbre uses. `nodes`
 * holds every oscillator so they can all be stopped together; the per-partial /
 * FM-depth gain nodes between an oscillator and `gain` are reachable only from
 * those oscillators and are collected once the oscillators stop. `timbre` keys
 * the steal policy's per-timbre occupancy counts. `sample` marks a
 * pitched-sample voice — the only kind the sustain pedal damps or holds, and
 * the kind a steal fades out instead of hard-stopping (its flat gain carries no
 * scheduled envelope ramps that a retarget would fight).
 */
interface Voice {
  nodes: AudioScheduledSourceNode[];
  gain: GainNode;
  timbre: Timbre;
  sample: boolean;
  /**
   * Scheduled natural end of the voice. The realtime pool is pruned by each
   * source's onended event, but an offline graph is built entirely before
   * rendering — no onended ever fires while voices are being scheduled — so
   * the offline engine prunes by comparing this against the sim clock instead;
   * without it the pool would fill with long-finished voices and the steal
   * policy would chop voices that realtime playback keeps.
   */
  endsAt: number;
}

/**
 * One rail's continuously sounding glissando voice, alive while particles
 * touch the rail. Separate from the impact Voice pool: it has no envelope or
 * stealing — pitch and gain are retargeted every frame and the voice is
 * released after a short silent grace period. `oscs` are the pitched
 * oscillators (two for pad, one otherwise) whose frequency the glide drives;
 * `lfoDepth` (vib only) is retargeted alongside so the vibrato depth stays a
 * constant pitch fraction. `lastActive` is the ctx time of the last frame with
 * contact, the release timer's anchor.
 */
interface RailVoice {
  oscs: OscillatorNode[];
  lfo: OscillatorNode | null;
  lfoDepth: GainNode | null;
  filter: BiquadFilterNode;
  gain: GainNode;
  timbre: RailTimbre;
  lastActive: number;
}

/**
 * Per-frame rail state the engine turns into a sustained voice: `count` is the
 * mean number of touching particles (drives loudness), `avgT` their mean arc
 * position in [0,1] (drives pitch between the start/end pitch indices).
 */
export interface RailVoiceInput {
  id: string;
  count: number;
  avgT: number;
  startPitchIndex: number;
  endPitchIndex: number;
  volume: number;
  railTimbre: RailTimbre;
}

/**
 * The per-frame audio entry points the simulation step drives. AudioEngine is
 * the realtime implementation; the loop-video export substitutes its own sink
 * so a capture run never sounds through the realtime context.
 */
export interface SimAudioSink {
  trigger(counts: Float32Array, impacts: Float32Array, objects: readonly PlacedObject[], windowSim: number): void;
  updateRailVoices(rails: readonly RailVoiceInput[]): void;
  playTap(object: PlacedObject): void;
}

interface TimbrePartial {
  ratio: number;
  level: number;
}

/**
 * A resolved ADSR envelope for one timbre, in absolute seconds plus a [0,1]
 * sustain level (the envelope knobs' normalized values are mapped to seconds by
 * the caller). Set per timbre by a wired envelope object; absence means the
 * timbre keeps its recipe AD. For a one-shot (impulse) voice there is no
 * note-off, so the voice plays A → D (to the sustain level) → R straight through.
 */
export interface ADSR {
  attackS: number;
  decayS: number;
  sustainLevel: number;
  releaseS: number;
}

const ENV_GAIN_FLOOR = 0.0001;
const ENV_MIN_SEG_S = 0.001;

type TimbreRecipe =
  | { synth: "subtractive"; oscType: OscillatorType; attack: number; decay: number; transpose: number; level: number }
  | { synth: "additive"; oscType: OscillatorType; partials: readonly TimbrePartial[]; attack: number; decay: number; transpose: number; level: number }
  | { synth: "fm"; modRatio: number; modIndex: number; attack: number; decay: number; transpose: number; level: number }
  | { synth: "sample"; zones: readonly SampleZone[]; level: number; randomDetuneCents: number }
  | { synth: "kit"; pieces: readonly string[]; pieceLabels: readonly string[]; level: number; randomDetuneCents: number }
  | { synth: "mic"; level: number; randomDetuneCents: number };

type SynthTimbreRecipe = Exclude<TimbreRecipe, { synth: "sample" | "kit" | "mic" }>;

/**
 * Synthesis parameters per timbre, kept in one table so audible tuning is a
 * data edit, not a code change. `tone` reuses the global AD constants so legacy
 * scenes sound unchanged. `wood` is a single xylophone sample zone stretched
 * across the register by detune; `bell` is FM with an inharmonic
 * carrier:modulator ratio and a long metallic ring. Times are seconds; `level`
 * is relative (normalized at build so partials sum to unit peak); `modIndex` is
 * peak carrier deviation as a multiple of the carrier frequency. `transpose`
 * shifts the timbre's whole register by that many octaves while leaving the
 * scale-degree mapping intact — a glockenspiel sits two octaves up like the
 * real instrument, so degree order is preserved but the voice reads bright.
 * `level` trims a timbre's output (<=1) so timbres balance by ear: bell sits
 * below unity because its high register and long ring read louder at equal peak.
 */
export interface TimbreCatalogEntry {
  id: Timbre;
  name: string;
  recipe: TimbreRecipe;
}

/**
 * Timbre catalog: the single source of truth for which timbres exist, their
 * display names, and synthesis recipes. Scenes persist timbre ids, so ids must
 * stay stable (no rename/reuse). Catalog order is free to change: the index
 * (0..N-1) is a runtime wiring value for the `timbre` int param, not a persisted
 * compatibility contract.
 */
export const TIMBRE_CATALOG: readonly TimbreCatalogEntry[] = [
  { id: "tone", name: "Tone", recipe: { synth: "subtractive", oscType: "triangle", attack: AUDIO_ATTACK_S, decay: AUDIO_DECAY_S, transpose: 0, level: 1 } },
  {
    id: "wood",
    name: "Wood",
    recipe: {
      synth: "sample",
      zones: [{ url: "samples/otologic_xylophone_067.m4a", rootMidi: 67 }],
      level: 1,
      randomDetuneCents: 1,
    },
  },
  {
    id: "bell",
    name: "Bell",
    recipe: {
      synth: "fm",
      modRatio: AUDIO_BELL_MOD_RATIO,
      modIndex: AUDIO_BELL_MOD_INDEX,
      attack: 0.002,
      decay: 2.3,
      transpose: AUDIO_BELL_TRANSPOSE,
      level: AUDIO_BELL_LEVEL,
    },
  },
  {
    id: "piano",
    name: "Piano",
    recipe: {
      synth: "sample",
      zones: [
        { url: "samples/salamander_piano_036.m4a", rootMidi: 36 },
        { url: "samples/salamander_piano_048.m4a", rootMidi: 48 },
        { url: "samples/salamander_piano_060.m4a", rootMidi: 60 },
        { url: "samples/salamander_piano_072.m4a", rootMidi: 72 },
        { url: "samples/salamander_piano_084.m4a", rootMidi: 84 },
      ],
      level: 0.9,
      randomDetuneCents: 1,
    },
  },
  {
    id: "drum",
    name: "Drum",
    recipe: {
      synth: "kit",
      pieces: [
        "samples/maou_se_inst_drum2_kick.m4a",
        "samples/maou_se_inst_drum2_tom3.m4a",
        "samples/maou_se_inst_drum2_tom2.m4a",
        "samples/maou_se_inst_drum2_tom1.m4a",
        "samples/maou_se_inst_drum2_snare.m4a",
        "samples/maou_se_inst_drum2_hat.m4a",
        "samples/maou_se_inst_drum2_cymbal.m4a",
      ],
      pieceLabels: KIT_PIECES,
      level: 1.25,
      randomDetuneCents: 30,
    },
  },
  {
    id: "mic",
    name: "Mic",
    recipe: {
      synth: "mic",
      level: 1,
      randomDetuneCents: 0,
    },
  },
];

/** Piece labels for a kit timbre (e.g. drum), or null when the timbre is not a kit. */
export function kitPieceLabelsFor(id: Timbre): readonly string[] | null {
  const entry = TIMBRE_CATALOG.find((e: TimbreCatalogEntry): boolean => e.id === id);
  return entry !== undefined && entry.recipe.synth === "kit" ? entry.recipe.pieceLabels : null;
}

/**
 * Whether a timbre's pitchIndex selects a piece rather than an audible note —
 * the catalog kits (drum) and `mic`, whose degree picks one of the
 * shared recordings. So a transpose must leave its index untouched
 * (effectivePitchIndex) and it has no semitone pitch buttons (pitchButtons).
 * `mic` is named here rather than via the catalog because its piece list is the
 * runtime mic store, not a static `kit` recipe.
 */
export function isKitTimbre(id: Timbre): boolean {
  return id === "mic" || kitPieceLabelsFor(id) !== null;
}

/** Catalog index of a timbre id (0 when unknown, so a stale id resolves to the first timbre). */
export function timbreIndexById(id: Timbre): number {
  const i = TIMBRE_CATALOG.findIndex((e: TimbreCatalogEntry): boolean => e.id === id);
  return i < 0 ? 0 : i;
}

/** Timbre id at a catalog index, clamped into range. */
export function timbreIdByIndex(index: number): Timbre {
  const clamped = Math.min(Math.max(Math.round(index), 0), TIMBRE_CATALOG.length - 1);
  return TIMBRE_CATALOG[clamped].id;
}

/** Display name for a timbre id (the id itself when unknown). */
export function timbreName(id: Timbre): string {
  return TIMBRE_CATALOG.find((e: TimbreCatalogEntry): boolean => e.id === id)?.name ?? id;
}

/** Whether an id names a catalog timbre. */
export function isTimbreId(id: string): boolean {
  return TIMBRE_CATALOG.some((e: TimbreCatalogEntry): boolean => e.id === id);
}

/** All timbre ids in catalog order. */
export function timbreIds(): Timbre[] {
  return TIMBRE_CATALOG.map((e: TimbreCatalogEntry): Timbre => e.id);
}

/** Margin between a synth voice's envelope end and its oscillators' hard stop. */
const SYNTH_STOP_PAD_S = 0.02;

function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Maps an OscillatorType to the synth-worklet wave code (sine 0 / triangle 1 / sawtooth 2 / square 3). */
function oscTypeToWave(oscType: OscillatorType): number {
  if (oscType === "triangle") {
    return 1;
  }
  if (oscType === "sawtooth") {
    return 2;
  }
  if (oscType === "square") {
    return 3;
  }
  return 0;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge1 <= edge0) {
    return x < edge0 ? 0 : 1;
  }
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * The mic timbre's live recording buffers, in play order (a null entry is a
 * recording that has not finished decoding). Injected so the engine reads the
 * shared mic store without importing it — the store imports the engine for the
 * `Timbre` type, so the dependency must point that way only.
 */
export interface MicBufferSource {
  buffersInOrder(): (AudioBuffer | null)[];
  trimsInOrder(): ({ start: number; end: number } | null)[];
}

export class AudioEngine {
  private readonly ctx: BaseAudioContext;
  private readonly timeSource: (() => number) | null;
  private readonly master: GainNode;
  private readonly limiter: DynamicsCompressorNode;
  /**
   * Final-stage 1st-order highpass on the whole music output (after the
   * limiter, before destination). Strips DC offset and sub-audible transients
   * that jerk the speaker cone into an audible pop on Android speakers, while
   * being transparent to musical content above ~20Hz. Sits downstream of the
   * limiter so reverb-return summing is also filtered; the keep-alive signal
   * routes straight to destination and bypasses it.
   */
  private readonly dcBlock: BiquadFilterNode;
  private readonly convolver: ConvolverNode;
  private readonly reverbReturn: GainNode;
  private readonly voices: Voice[];
  private readonly railVoices: Map<string, RailVoice>;
  private readonly nextFire: Float32Array;
  private readonly pendingPeak: Float32Array;
  private readonly pendingImpact: Float32Array;
  private onsetAnchorOffset: number = 0;
  private onsetAnchorInit: boolean = false;
  private lastAnchorSim: number = -1;
  private masterVolume: number;
  private readonly recipe: Record<Timbre, TimbreRecipe>;
  private readonly density: Float32Array;
  private readonly rateEma: Float32Array;
  private lastTriggerTime: number;
  private lastRailUpdateTime: number;
  private windowMuted: boolean;
  private densityRateLow: number;
  private densityRateHigh: number;
  private densityTransient: number;
  private densityDetuneCents: number;
  private densityGainFloor: number;
  private densityDuckFloor: number;
  private reverbAmount: number;
  private sceneTimbreOverride: Timbre | null;
  private readonly timbreTranspose: Record<Timbre, number>;
  private readonly timbreEnvelope: Partial<Record<Timbre, ADSR>>;
  private readonly synthWorkletNodes: Map<Timbre, AudioWorkletNode>;
  private readonly sampleWorkletNodes: Map<Timbre, AudioWorkletNode>;
  private readonly sampleWorkletReady: Set<Timbre>;
  private readonly sampleUrlsByTimbre: Map<Timbre, string[]>;
  private readonly transferredUrls: Set<string>;
  private readonly lastSentSustain: Map<Timbre, number>;
  private onSampleDecoded: ((url: string) => void) | null;
  private readonly sampleBuffers: Map<string, AudioBuffer>;
  private readonly kitChoke: Map<number, Voice>;
  private readonly sustainValues: Map<Timbre, number>;
  private readonly heldVoices: Map<Timbre, Set<Voice>>;
  private readonly timbreBus: Record<Timbre, GainNode>;
  private readonly timbreVolumes: Record<Timbre, number>;
  private micSource: MicBufferSource | null;
  private prewarmedSynth: boolean;
  private prewarmedSample: boolean;
  private sampleDecodeOk: number = 0;

  /**
   * Default-constructed the engine is realtime over its own AudioContext. The
   * loop-video export passes an OfflineAudioContext plus `timeSource` (the
   * capture run's sim clock — every schedule anchor reads it instead of
   * ctx.currentTime, which never advances while an offline graph is being
   * built) and the realtime engine's decoded sample map (an offline run must
   * not re-fetch; AudioBuffers are context-independent at equal sample rates).
   */
  constructor(
    ctx: BaseAudioContext = new AudioContext(),
    timeSource: (() => number) | null = null,
    decodedSamples: ReadonlyMap<string, AudioBuffer> | null = null,
  ) {
    this.ctx = ctx;
    this.timeSource = timeSource;
    this.master = this.ctx.createGain();
    this.master.gain.value = AUDIO_MASTER_GAIN;
    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.value = LIMITER_THRESHOLD_DB;
    this.limiter.knee.value = LIMITER_KNEE_DB;
    this.limiter.ratio.value = LIMITER_RATIO;
    this.limiter.attack.value = LIMITER_ATTACK_S;
    this.limiter.release.value = LIMITER_RELEASE_S;
    this.dcBlock = this.ctx.createBiquadFilter();
    this.dcBlock.type = "highpass";
    this.dcBlock.frequency.value = AUDIO_DCBLOCK_HZ;
    this.master.connect(this.limiter);
    this.limiter.connect(this.dcBlock);
    this.dcBlock.connect(this.ctx.destination);
    this.convolver = this.ctx.createConvolver();
    this.convolver.buffer = this.buildReverbIR();
    this.reverbReturn = this.ctx.createGain();
    this.reverbReturn.gain.value = 0;
    this.master.connect(this.convolver);
    this.convolver.connect(this.reverbReturn);
    this.reverbReturn.connect(this.limiter);
    this.voices = [];
    this.railVoices = new Map<string, RailVoice>();
    this.nextFire = new Float32Array(MAX_OBJECTS);
    this.pendingPeak = new Float32Array(MAX_OBJECTS);
    this.pendingImpact = new Float32Array(MAX_OBJECTS);
    this.masterVolume = AUDIO_MASTER_GAIN;
    this.recipe = {};
    for (const entry of TIMBRE_CATALOG) {
      this.recipe[entry.id] = { ...entry.recipe };
    }
    this.sampleBuffers = new Map<string, AudioBuffer>(decodedSamples ?? []);
    this.kitChoke = new Map<number, Voice>();
    this.sustainValues = new Map<Timbre, number>();
    this.heldVoices = new Map<Timbre, Set<Voice>>();
    const mkBus = (): GainNode => {
      const g = this.ctx.createGain();
      g.gain.value = 1;
      g.connect(this.master);
      return g;
    };
    this.timbreBus = {};
    this.timbreVolumes = {};
    for (const entry of TIMBRE_CATALOG) {
      this.timbreBus[entry.id] = mkBus();
      this.timbreVolumes[entry.id] = 1;
    }
    if (decodedSamples === null) {
      this.preloadSamples();
    }
    this.density = new Float32Array(MAX_OBJECTS);
    this.rateEma = new Float32Array(MAX_OBJECTS);
    this.lastTriggerTime = 0;
    this.lastRailUpdateTime = 0;
    this.windowMuted = false;
    this.densityRateLow = AUDIO_DENSITY_RATE_LOW;
    this.densityRateHigh = AUDIO_DENSITY_RATE_HIGH;
    this.densityTransient = AUDIO_DENSITY_TRANSIENT;
    this.densityDetuneCents = AUDIO_DENSITY_DETUNE_CENTS;
    this.densityGainFloor = AUDIO_DENSITY_GAIN_FLOOR;
    this.densityDuckFloor = AUDIO_DENSITY_DUCK_FLOOR;
    this.reverbAmount = REVERB_AMOUNT_DEFAULT;
    this.sceneTimbreOverride = null;
    this.timbreTranspose = {};
    this.timbreEnvelope = {};
    this.synthWorkletNodes = new Map<Timbre, AudioWorkletNode>();
    this.sampleWorkletNodes = new Map<Timbre, AudioWorkletNode>();
    this.sampleWorkletReady = new Set<Timbre>();
    this.sampleUrlsByTimbre = new Map<Timbre, string[]>();
    this.transferredUrls = new Set<string>();
    this.lastSentSustain = new Map<Timbre, number>();
    this.onSampleDecoded = null;
    this.micSource = null;
    this.prewarmedSynth = false;
    this.prewarmedSample = false;
  }

  /** Injects the shared mic recording store so `mic`-timbre voices read its live buffers. */
  setMicSource(source: MicBufferSource): void {
    this.micSource = source;
  }

  /** Decodes encoded audio (mic recordings) on the engine's context for context-matched playback. */
  decodeAudio(data: ArrayBuffer): Promise<AudioBuffer> {
    return this.ctx.decodeAudioData(data);
  }

  /**
   * Default realtime path (?worklet=0 to opt out): routes the synth timbres (fm / subtractive /
   * additive — bell, tone, wood) through a static-graph AudioWorklet voice pool
   * instead of per-note OscillatorNodes, removing the topology-change glitch on
   * contact. One node per synth timbre is connected to that timbre's existing
   * bus, so per-timbre volume and the downstream master/limiter/reverb stay
   * effective. The offline export engine never uses this (it has a timeSource);
   * until a timbre's node is ready, that timbre falls back to the native path
   * in `playVoice`. Sample/kit timbres take the parallel static-graph sample
   * worklet set up here (stereo voice pool, PCM transferred once decode lands);
   * each falls back to native until its PCM has been transferred.
   */
  async enableSynthWorklet(): Promise<void> {
    if (this.timeSource !== null || !(this.ctx instanceof AudioContext)) {
      return;
    }
    await this.ctx.audioWorklet.addModule(synthWorkletUrl);
    await this.ctx.audioWorklet.addModule(sampleWorkletUrl);
    for (const entry of TIMBRE_CATALOG) {
      if (entry.recipe.synth === "sample" || entry.recipe.synth === "kit") {
        continue;
      }
      const node = new AudioWorkletNode(this.ctx, "synth-voice", { outputChannelCount: [1] });
      node.connect(this.timbreBus[entry.id]);
      this.synthWorkletNodes.set(entry.id, node);
    }
    this.setupSampleWorklet();
    this.maybePrewarm();
  }

  /**
   * Creates one stereo sample-voice node per sample/kit timbre (so per-timbre
   * volume and the downstream master chain stay effective), then transfers each
   * timbre's decoded PCM once. Decode is asynchronous and may finish after this
   * call: already-decoded buffers transfer immediately, the rest transfer via
   * the `onSampleDecoded` hook preloadSamples fires on completion. A timbre is
   * marked ready (so playVoice routes to the worklet) only once every sample it
   * needs has been transferred.
   */
  private setupSampleWorklet(): void {
    for (const entry of TIMBRE_CATALOG) {
      const recipe = this.recipe[entry.id];
      if (recipe.synth !== "sample" && recipe.synth !== "kit") {
        continue;
      }
      const node = new AudioWorkletNode(this.ctx, "sample-voice", { outputChannelCount: [2] });
      node.connect(this.timbreBus[entry.id]);
      node.port.postMessage({
        type: "config", voiceCap: VOICE_CAP, chokeFadeS: CHOKE_FADE_S,
        onsetFadeS: SAMPLE_ONSET_FADE_S,
        sustainDampHoldS: SUSTAIN_DAMP_HOLD_S, sustainDampTau: SUSTAIN_DAMP_TAU,
        sustainHoldThreshold: SUSTAIN_HOLD_THRESHOLD,
      });
      this.sampleWorkletNodes.set(entry.id, node);
      this.sampleUrlsByTimbre.set(entry.id, recipe.synth === "sample" ? recipe.zones.map((z: SampleZone): string => z.url) : [...recipe.pieces]);
    }
    this.onSampleDecoded = (url: string): void => {
      this.transferSample(url);
    };
    for (const urls of this.sampleUrlsByTimbre.values()) {
      for (const url of urls) {
        if (this.sampleBuffers.has(url)) {
          this.transferSample(url);
        }
      }
    }
  }

  /**
   * Transfers one decoded sample's PCM to every sample-worklet node that uses
   * it, keyed by the timbre-local id (its index in that timbre's url list).
   * Copies the channel data so the transferable detach never touches the
   * AudioBuffer the native fallback path still reads; mono samples send a
   * duplicated right channel so the worklet always reads two channels. Recomputes
   * per-timbre readiness so a timbre flips on once its last sample lands.
   */
  private transferSample(url: string): void {
    if (this.transferredUrls.has(url)) {
      return;
    }
    const buffer = this.sampleBuffers.get(url);
    if (buffer === undefined) {
      return;
    }
    for (const [timbre, urls] of this.sampleUrlsByTimbre) {
      const id = urls.indexOf(url);
      const node = this.sampleWorkletNodes.get(timbre);
      if (id < 0 || node === undefined) {
        continue;
      }
      const left = new Float32Array(buffer.getChannelData(0));
      const right = buffer.numberOfChannels > 1 ? new Float32Array(buffer.getChannelData(1)) : new Float32Array(left);
      node.port.postMessage({ type: "loadSample", id, left, right, length: buffer.length, sampleRate: buffer.sampleRate }, [left.buffer, right.buffer]);
    }
    this.transferredUrls.add(url);
    for (const [timbre, urls] of this.sampleUrlsByTimbre) {
      if (urls.every((u: string): boolean => this.transferredUrls.has(u))) {
        this.sampleWorkletReady.add(timbre);
      }
    }
    this.maybePrewarm();
  }

  /**
   * Scene-wide timbre override (simple mode plays everything as bell). Only
   * the sounding voice is overridden — per-object timbre data is untouched, so
   * clearing the override restores each object's own voice.
   */
  setSceneTimbreOverride(timbre: Timbre | null): void {
    this.sceneTimbreOverride = timbre;
  }

  /**
   * Per-timbre semitone transpose applied to a pitched voice at sounding time
   * (kit timbres excluded; see effectivePitchIndex). Runtime modulation only —
   * per-object stored pitch indices are never rewritten — so a timbre with no
   * governing timbre node sounds unshifted (lookup defaults to 0).
   */
  setTimbreTranspose(timbre: Timbre, semitones: number): void {
    this.timbreTranspose[timbre] = Math.round(semitones);
  }

  /**
   * Sets (or clears, on null) the ADSR a wired envelope object supplies for a
   * timbre. While set, playVoice shapes every voice of that timbre with this
   * envelope instead of the recipe AD; clearing restores the recipe.
   */
  setTimbreEnvelope(timbre: Timbre, adsr: ADSR | null): void {
    if (adsr === null) {
      delete this.timbreEnvelope[timbre];
    } else {
      this.timbreEnvelope[timbre] = adsr;
    }
  }

  /** Last value written to a timbre's sustain pedal (1 when never driven = full natural ring by default). */
  sustainValue(timbre: Timbre): number {
    return this.sustainValues.get(timbre) ?? 1;
  }

  /**
   * Ordered pool snapshot for the debug HUD, oldest voice first (index 0 of a
   * timbre is its next steal victim). `held` marks pedal-held voices. Rail
   * voices live outside the pool and are not included.
   */
  voiceSlots(): { timbre: Timbre; held: boolean }[] {
    return this.voices.map((voice: Voice): { timbre: Timbre; held: boolean } => ({
      timbre: voice.timbre,
      held: this.heldVoices.get(voice.timbre)?.has(voice) ?? false,
    }));
  }

  /**
   * Drives a timbre's continuous sustain pedal (clamped to 0..1). The sample
   * assets are pedal-down recordings (full natural ring), so the pedal works by
   * suppression: a voice triggered below the hold threshold is damped after a
   * hold time that scales exponentially with the value — 0 is the shortest
   * staccato touch, values toward 1 approach the full natural ring (half-pedal)
   * — while a voice triggered at or above SUSTAIN_HOLD_THRESHOLD rings the
   * full sample (the natural end is the maximum ring; no looping past it). The
   * mapping is per-trigger: a damped voice carries an irrevocable source.stop
   * reservation, so a later pedal change only affects subsequent strikes except
   * one transition: leaving the hold zone remaps all still-ringing held voices
   * onto a pedal-aware damper whose tail length follows the new value (lighter
   * release = longer tail, full release = shorter tail). Same-value writes are
   * cheap no-ops so the per-frame param overwrite can call this unconditionally.
   * Volatile: never persisted.
   */
  setSustainValue(timbre: Timbre, value: number): void {
    const nextPedal = Math.min(Math.max(value, 0), 1);
    const node = this.sampleWorkletNodes.get(timbre);
    if (node !== undefined && this.lastSentSustain.get(timbre) !== nextPedal) {
      node.port.postMessage({ type: "setSustain", pedal: nextPedal });
      this.lastSentSustain.set(timbre, nextPedal);
    }
    const wasHeldZone = this.sustainValue(timbre) >= SUSTAIN_HOLD_THRESHOLD;
    this.sustainValues.set(timbre, nextPedal);
    if (nextPedal >= SUSTAIN_HOLD_THRESHOLD || !wasHeldZone) {
      return;
    }
    const held = this.heldVoices.get(timbre);
    if (!held) {
      return;
    }
    this.heldVoices.delete(timbre);
    const now = this.now();
    for (const voice of held) {
      this.applyDamper(voice, nextPedal, now);
    }
  }

  private holdVoice(timbre: Timbre, voice: Voice): void {
    let held = this.heldVoices.get(timbre);
    if (!held) {
      held = new Set<Voice>();
      this.heldVoices.set(timbre, held);
    }
    held.add(voice);
  }

  private applyDamper(voice: Voice, pedal: number, when: number): void {
    const clampedPedal = Math.min(Math.max(pedal, 0), 1);
    const remaining = Math.max(voice.endsAt - when, 0);
    const hold = SUSTAIN_DAMP_HOLD_S * Math.pow(Math.max(remaining / SUSTAIN_DAMP_HOLD_S, 1), clampedPedal);
    const stopAt = when + hold + SUSTAIN_DAMP_TAU * 5;
    voice.gain.gain.setTargetAtTime(0, when + hold, SUSTAIN_DAMP_TAU);
    for (const node of voice.nodes) {
      try {
        node.stop(stopAt);
      } catch {
        // node already stopped naturally before the damper arrived
      }
    }
    voice.endsAt = Math.min(voice.endsAt, stopAt);
  }

  /**
   * Fetches and decodes every sample URL referenced by the current recipe set,
   * populating `sampleBuffers`. Fires at construction time — AudioContext may
   * still be suspended, but decodeAudioData works regardless of ctx.state.
   * Load failures are logged once and leave the entry absent so playback skips
   * silently rather than crashing. A running decode tally is logged once all
   * urls settle so a fully-failed load (no audible samples) is not silent.
   */
  private preloadSamples(): void {
    const urls = new Set<string>();
    for (const recipe of Object.values(this.recipe)) {
      if (recipe.synth === "sample") {
        for (const zone of recipe.zones) {
          urls.add(zone.url);
        }
      } else if (recipe.synth === "kit") {
        for (const piece of recipe.pieces) {
          urls.add(piece);
        }
      }
    }
    this.sampleDecodeOk = 0;
    let settled = 0;
    const total = urls.size;
    const onSettle = (): void => {
      settled++;
      if (settled === total) {
        console.log(`Audio samples decoded: ${this.sampleDecodeOk}/${total}`);
      }
    };
    for (const url of urls) {
      fetch(url)
        .then((r: Response): Promise<ArrayBuffer> => {
          if (!r.ok) {
            throw new Error(`HTTP ${r.status}`);
          }
          return r.arrayBuffer();
        })
        .then((ab: ArrayBuffer): Promise<AudioBuffer> => this.ctx.decodeAudioData(ab))
        .then((buf: AudioBuffer): void => {
          this.sampleBuffers.set(url, buf);
          this.sampleDecodeOk++;
          this.onSampleDecoded?.(url);
        })
        .catch((e: unknown): void => {
          console.error(`Sample load failed: ${url}`, e);
        })
        .finally(onSettle);
    }
  }

  /**
   * Builds the shared reverb impulse response once at construction: per-channel
   * independent exponential-decay white noise so the two channels decorrelate
   * and the wash reads with stereo width. The noise waveform varies per session
   * (Math.random) but is generated a single time, so it carries no per-hit
   * nondeterminism — the density crossfade tolerates this axis being non-physical.
   */
  private buildReverbIR(): AudioBuffer {
    const rate = this.ctx.sampleRate;
    const length = Math.floor(rate * REVERB_IR_SECONDS);
    const buffer = this.ctx.createBuffer(2, length, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        const t = i / rate;
        data[i] = (Math.random() * 2 - 1) * Math.exp(-t / REVERB_IR_DECAY);
      }
    }
    return buffer;
  }

  /**
   * The schedule anchor every voice / ramp uses: the capture run's sim clock
   * when one is injected, the audio clock otherwise. Offline graphs are built
   * entirely before rendering, so ctx.currentTime would sit at 0 throughout.
   */
  private now(): number {
    return this.timeSource ? this.timeSource() : this.ctx.currentTime;
  }

  /**
   * Maps a sim clock time (particle `elapsed`) to ctx time through the onset
   * clock anchor: ctxForSim(s) = s + onsetAnchorOffset. The offset is the slow
   * baseline of (now - windowSim), re-tracked once per fresh contact window via a
   * low-pass so it follows clock drift (the accumulator-clamp deficit a stalled
   * frame leaves between elapsed and ctx.currentTime) while rejecting per-delivery
   * jitter. It is never snapped to a new value — a discontinuous re-anchor would
   * shift a scheduled onset audibly. Realtime engine only; the offline export
   * engine reads a deterministic sim clock as `now` and keeps frame-time onsets.
   */
  private updateOnsetAnchor(windowSim: number, now: number): void {
    if (windowSim === this.lastAnchorSim) {
      return;
    }
    const lag = now - windowSim;
    if (this.onsetAnchorInit) {
      this.onsetAnchorOffset += AUDIO_ONSET_ANCHOR_FOLLOW * (lag - this.onsetAnchorOffset);
    } else {
      this.onsetAnchorOffset = lag;
      this.onsetAnchorInit = true;
    }
    this.lastAnchorSim = windowSim;
  }

  /**
   * Whether per-frame entries may schedule. An OfflineAudioContext reports
   * "suspended" until startRendering, so the offline engine bypasses the state
   * gate that protects the realtime engine before the first user gesture.
   */
  private running(): boolean {
    return this.timeSource !== null || this.ctx.state === "running";
  }

  /** Decoded sample map for handing to an offline engine; decode happens once per page. */
  decodedSamples(): ReadonlyMap<string, AudioBuffer> {
    return this.sampleBuffers;
  }

  sampleRate(): number {
    return this.ctx.sampleRate;
  }

  resume(): void {
    if (this.ctx instanceof AudioContext && this.ctx.state !== "running") {
      void this.ctx.resume().then((): void => {
        this.maybePrewarm();
        if (this.ctx.state === "running") {
          track("audio_start");
        }
      });
    } else {
      this.maybePrewarm();
    }
  }

  /**
   * Fires one inaudible note per ready worklet timbre the first time the audio
   * context is running, so V8 tier-compiles each worklet process()
   * active-synthesis branch during silence. On mobile the worklet's first
   * active render misses the tight render-quantum deadline once (a single
   * PushPullFIFO underflow, measured ~0.7s into the first sound), audible as a
   * startup click on speakers; spending that warmup on silent notes keeps the
   * first real note glitch-free, without the output latency a larger
   * latencyHint would add. A burst of AUDIO_PREWARM_VOICES per timbre (not one
   * note) is needed so the multi-voice process() loop reaches the same V8
   * tier-up it hits under real polyphony — a single warm note left a residual
   * underflow a few seconds into sustained play. Synth and sample processors
   * warm independently: each is a separate AudioWorkletGlobalScope code path and
   * sample readiness lands asynchronously after decode/transfer.
   */
  private maybePrewarm(): void {
    if (this.ctx.state !== "running") {
      return;
    }
    const when = this.now() + AUDIO_LOOKAHEAD_S;
    if (!this.prewarmedSynth && this.synthWorkletNodes.size > 0) {
      for (const timbre of this.synthWorkletNodes.keys()) {
        for (let i = 0; i < AUDIO_PREWARM_VOICES; i++) {
          this.playVoice(i % 12, AUDIO_PREWARM_GAIN, when, timbre, 0);
        }
      }
      this.prewarmedSynth = true;
    }
    if (!this.prewarmedSample) {
      const ready = this.sampleWorkletReady.values().next().value;
      if (ready !== undefined) {
        for (let i = 0; i < AUDIO_PREWARM_VOICES; i++) {
          this.playVoice(i % 12, AUDIO_PREWARM_GAIN, when, ready, 0);
        }
        this.prewarmedSample = true;
      }
    }
  }

  setMasterVolume(value: number): void {
    this.masterVolume = value;
    this.master.gain.value = value;
  }

  timbreVolume(t: Timbre): number {
    return this.timbreVolumes[t];
  }

  setTimbreVolume(t: Timbre, v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    this.timbreVolumes[t] = clamped;
    this.timbreBus[t].gain.setTargetAtTime(clamped, this.now(), AUDIO_DENSITY_DUCK_TAU);
  }

  /**
   * Fades the master bus on window blur/focus. trigger() keeps re-targeting
   * the master gain every frame, so the muted state must also gate that path —
   * a one-shot ramp here alone would be overwritten on the next frame.
   */
  setWindowMuted(muted: boolean): void {
    if (this.windowMuted === muted) {
      return;
    }
    this.windowMuted = muted;
    const target = muted ? 0 : this.masterVolume;
    this.master.gain.setTargetAtTime(target, this.now(), AUDIO_BLUR_FADE_TAU);
  }

  setWoodDecay(seconds: number): void {
    const wood = this.recipe.wood;
    if (wood.synth === "additive") {
      wood.decay = seconds;
    }
  }

  setBellTranspose(octaves: number): void {
    const bell = this.recipe.bell;
    if (bell.synth === "fm") {
      bell.transpose = octaves;
    }
  }

  setBellModRatio(ratio: number): void {
    const bell = this.recipe.bell;
    if (bell.synth === "fm") {
      bell.modRatio = ratio;
    }
  }

  setBellModIndex(index: number): void {
    const bell = this.recipe.bell;
    if (bell.synth === "fm") {
      bell.modIndex = index;
    }
  }

  setBellLevel(level: number): void {
    this.recipe.bell.level = level;
  }

  setDensityLow(rate: number): void {
    this.densityRateLow = rate;
  }

  setDensityHigh(rate: number): void {
    this.densityRateHigh = rate;
  }

  setDensityTransient(amount: number): void {
    this.densityTransient = amount;
  }

  setDensityDetune(cents: number): void {
    this.densityDetuneCents = cents;
  }

  setDensityVoiceGain(floor: number): void {
    this.densityGainFloor = floor;
  }

  setDensityDuck(floor: number): void {
    this.densityDuckFloor = floor;
  }

  setReverbAmount(amount: number): void {
    this.reverbAmount = amount;
  }

  /**
   * Per-frame entry: one representative voice per object, grid-locked to a
   * per-object cooldown so static contact does not machine-gun. Each object
   * carries the next grid time (nextFire); a voice is reserved exactly at that
   * time via osc.start(fireTime), so sustained contact fires at sample-accurate
   * COOLDOWN spacing independent of frame phase. LOOKAHEAD bounds how far ahead
   * of the grid a reservation may be made within a frame. Contacts during a
   * cooldown are not dropped — their peak count is held in pendingPeak until the
   * slot opens; contacts arriving after a reservation roll into the next window.
   *
   * A single hit (sequencer step, cooldown long lapsed so nextFire is in the
   * past) would otherwise schedule at the frame-arrival `now`, requantizing the
   * onset to whichever frame the contact window was delivered on — the ±1-frame
   * rhythm jitter the sequencer exposes. Instead it reserves at the contact
   * window's sim time mapped through the clock anchor plus AUDIO_ONSET_LATENCY_S
   * (windowSim is fixed-step derived, so this is independent of frame phase /
   * delivery frame). Sustained grid-locked fires (nextFire still ahead of now)
   * are untouched. Invariant: the reservation is clamped to >= now (a target in
   * the past — the first hit's absolute latency, or anchor drift exceeding the
   * budget — degrades to `now`, never schedules behind the clock). The offline
   * export engine has a deterministic sim `now` with no frame jitter, so it keeps
   * the plain frame-time schedule (useSimAnchor false).
   * Gain combines the window's peak onset count (compressed by AUDIO_COUNT_EXP)
   * with the strongest per-frame mean normal-approach speed (impact); harder hits are
   * louder and gentle contacts fade toward silence. Result is clamped. Objects
   * at volume 0 are skipped and stay silent. Rails are skipped entirely — their
   * sound is a separate sustained voice, not the impact system.
   *
   * `density[i]` (0..1) is object i's own smoothed firing rate, recomputed each
   * frame from its leaky-integrated rate *before* this frame's fire is added, so
   * a fire only raises that object's next-frame density — feeding the fire count
   * straight back into its own density would oscillate. Per-voice shaping reads
   * density[i], so a quietly-struck object keeps its clear attack while a busy
   * neighbor blurs; the master duck and reverb send read the per-frame max
   * density so the bus still tracks the scene's loudest object.
   *
   * The scheduling lookahead widens to the observed call interval: when rAF is
   * throttled (unfocused window), a fixed 30ms window would defer fires past
   * their nextFire and quantize onsets to frame boundaries.
   */
  trigger(counts: Float32Array, impacts: Float32Array, objects: readonly PlacedObject[], windowSim: number): void {
    if (!this.running()) {
      return;
    }
    const now = this.now();
    const dt = this.lastTriggerTime > 0 ? now - this.lastTriggerTime : 0;
    const decay = Math.exp(-dt / AUDIO_DENSITY_TAU);
    this.lastTriggerTime = now;
    const lookahead = Math.max(AUDIO_LOOKAHEAD_S, dt);
    const cd = AUDIO_COOLDOWN_MS / 1000;
    const useSimAnchor = this.timeSource === null && AUDIO_ONSET_LOCK;
    if (useSimAnchor) {
      this.updateOnsetAnchor(windowSim, now);
    }
    const limit = Math.min(objects.length, MAX_OBJECTS);
    let dAgg = 0;
    for (let i = 0; i < limit; i++) {
      this.rateEma[i] *= decay;
      const density = smoothstep(this.densityRateLow, this.densityRateHigh, this.rateEma[i]);
      this.density[i] = density;
      if (density > dAgg) {
        dAgg = density;
      }
      if (objects[i].type === "rail" || objects[i].muted || objects[i].volume <= 0) {
        continue;
      }
      if (counts[i] > this.pendingPeak[i]) {
        this.pendingPeak[i] = counts[i];
      }
      const frameImpact = impacts[i] / Math.max(counts[i], 1);
      if (frameImpact > this.pendingImpact[i]) {
        this.pendingImpact[i] = frameImpact;
      }
      if (this.pendingPeak[i] <= 0 || now + lookahead < this.nextFire[i]) {
        continue;
      }
      const fireTime =
        this.nextFire[i] > now
          ? this.nextFire[i]
          : useSimAnchor
            ? Math.max(windowSim + this.onsetAnchorOffset + AUDIO_ONSET_LATENCY_S, now)
            : now;
      const gain = this.combineGain(this.pendingPeak[i], this.pendingImpact[i], density) * objects[i].volume;
      this.pendingPeak[i] = 0;
      this.pendingImpact[i] = 0;
      const effPitch = effectivePitchIndex(objects[i].pitchIndex, objects[i].timbreId, this.timbreTranspose[objects[i].timbreId] ?? 0);
      this.playVoice(effPitch, gain, fireTime, this.sceneTimbreOverride ?? objects[i].timbreId, density);
      if (ONSET_DIAG) {
        recordOnsetFire(now, fireTime, windowSim, i);
      }
      this.rateEma[i] += 1 / AUDIO_DENSITY_TAU;
      this.nextFire[i] = fireTime + cd;
    }
    const duck = lerp(1, this.densityDuckFloor, dAgg);
    if (!this.windowMuted) {
      this.master.gain.setTargetAtTime(this.masterVolume * duck, now, AUDIO_DENSITY_DUCK_TAU);
    }
    const send = this.reverbAmount * dAgg * REVERB_SEND_MAX;
    this.reverbReturn.gain.setTargetAtTime(send, now, REVERB_SEND_TAU);
  }

  /**
   * Plays a deliberate performance-mode tap as its own note at fixed full
   * volume. A tap is explicit input, so it bypasses the per-object contact
   * cooldown (which would otherwise merge it into the frame's particle-driven
   * note) and the texture-density droop — it must sound distinctly even while
   * particles are already firing the same object. Scheduled AUDIO_LOOKAHEAD_S
   * ahead like contact notes: a note started at the bare current time lands in
   * the past by the worklet's next render block, so the voice's onset fade-in
   * (and synth attack ramp) is skipped and the non-zero first sample steps the
   * output — audible as a click on phone speakers.
   */
  playTap(object: PlacedObject): void {
    if (!this.running() || object.muted || object.volume <= 0) {
      return;
    }
    const gain = AUDIO_GAIN_PER_HIT * object.volume;
    const effPitch = effectivePitchIndex(object.pitchIndex, object.timbreId, this.timbreTranspose[object.timbreId] ?? 0);
    this.playVoice(effPitch, gain, this.now() + AUDIO_LOOKAHEAD_S, this.sceneTimbreOverride ?? object.timbreId, 0);
  }

  /**
   * Plays one Bell note for a title-screen letter hit. The title animation runs
   * before the editor scene exists, so it has no PlacedObject to route through
   * playTap/trigger; this is the thin PlacedObject-free entry into the same
   * worklet voice pool (never the per-note node path, which pops on mobile
   * onset). The title sim starts only after the click-to-play gesture unlocks
   * audio (see titleAudioReady), so the running() guard is a safety net for
   * the ready-wait timeout path, not the normal flow.
   */
  /**
   * True once title bells can actually sound: the context is running (user
   * gesture or autoplay-permitted) and the bell synth worklet node exists, so
   * `playTitleBell` neither no-ops nor falls back to the per-note path. The
   * title screen polls this to delay its sim until bells ring from the start.
   */
  titleAudioReady(): boolean {
    return this.running() && this.synthWorkletNodes.has("bell");
  }

  playTitleBell(pitchIndex: number, velocity: number): void {
    if (!this.running()) {
      return;
    }
    const gain = AUDIO_GAIN_PER_HIT * Math.max(0, Math.min(1, velocity));
    this.playVoice(pitchIndex, gain, this.now() + AUDIO_LOOKAHEAD_S, "bell", 0);
  }

  /**
   * Per-frame entry for the rail glissando voices. Each touched rail keeps one
   * triangle+lowpass voice whose frequency glides along the start→end pitch
   * (midi-linear, so the slide is smooth and unquantized) and whose gain
   * follows sqrt(count) — denser contact is louder but compressed, clamped so
   * a flooded rail cannot blow up the bus. setTargetAtTime on both params
   * prevents zipper noise. A rail with no contact ramps to silence and is
   * disposed after a short grace period; voices whose rail vanished from the
   * input list (deleted / undone) are disposed immediately. Independent of the
   * density texture layer — rails do not feed or read `density`.
   *
   * The ramp taus widen to half the observed call interval: when rAF is
   * throttled (unfocused window), the fixed taus complete within each gap and
   * the glide degrades into audible stair-steps.
   */
  updateRailVoices(rails: readonly RailVoiceInput[]): void {
    if (!this.running()) {
      return;
    }
    const now = this.now();
    const railDt = this.lastRailUpdateTime > 0 ? now - this.lastRailUpdateTime : 0;
    this.lastRailUpdateTime = now;
    const freqTau = Math.max(RAIL_VOICE_FREQ_TAU, railDt * 0.5);
    const gainTau = Math.max(RAIL_VOICE_GAIN_TAU, railDt * 0.5);
    const seen = new Set<string>();
    for (const rail of rails) {
      seen.add(rail.id);
      const active = rail.count > 0 && rail.volume > 0;
      let voice = this.railVoices.get(rail.id);
      if (voice && voice.timbre !== rail.railTimbre) {
        this.killRailVoice(voice);
        this.railVoices.delete(rail.id);
        voice = undefined;
      }
      if (!voice) {
        if (!active) {
          continue;
        }
        voice = this.createRailVoice(rail.railTimbre, this.railFreq(rail), now);
        this.railVoices.set(rail.id, voice);
      }
      if (!active) {
        voice.gain.gain.setTargetAtTime(0, now, gainTau);
        if (now - voice.lastActive > RAIL_VOICE_RELEASE_S) {
          this.killRailVoice(voice);
          this.railVoices.delete(rail.id);
        }
        continue;
      }
      const freq = this.railFreq(rail);
      for (const osc of voice.oscs) {
        osc.frequency.setTargetAtTime(freq, now, freqTau);
      }
      voice.lfoDepth?.gain.setTargetAtTime(freq * RAIL_VIB_DEPTH_RATIO, now, freqTau);
      const gain = Math.min(Math.sqrt(rail.count) * RAIL_VOICE_GAIN_PER, RAIL_VOICE_GAIN_MAX) * rail.volume;
      voice.gain.gain.setTargetAtTime(gain, now, gainTau);
      voice.lastActive = now;
    }
    for (const [id, voice] of [...this.railVoices]) {
      if (!seen.has(id)) {
        this.killRailVoice(voice);
        this.railVoices.delete(id);
      }
    }
  }

  /**
   * Releases every rail voice with the fade and stop scheduled on the audio
   * clock. updateRailVoices is rAF-driven, so when the tab is hidden no frame
   * arrives to ramp gains down or stop oscillators — voices would otherwise
   * sustain indefinitely while Web Audio keeps rendering in the background.
   */
  releaseAllRailVoices(): void {
    const now = this.now();
    for (const voice of this.railVoices.values()) {
      voice.gain.gain.setTargetAtTime(0, now, RAIL_VOICE_GAIN_TAU);
      const stopAt = now + RAIL_VOICE_RELEASE_S;
      voice.lfo?.stop(stopAt);
      for (const osc of voice.oscs) {
        osc.stop(stopAt);
      }
      voice.oscs[0].onended = (): void => {
        voice.lfo?.disconnect();
        voice.lfoDepth?.disconnect();
        for (const osc of voice.oscs) {
          osc.disconnect();
        }
        voice.filter.disconnect();
        voice.gain.disconnect();
      };
    }
    this.railVoices.clear();
  }

  stopAllVoices(): void {
    this.releaseAllRailVoices();
    const now = this.now();
    for (const voice of [...this.voices]) {
      this.chokeVoice(voice, now);
    }
    this.heldVoices.clear();
    this.kitChoke.clear();
  }

  private railFreq(rail: RailVoiceInput): number {
    const t = Math.min(Math.max(rail.avgT, 0), 1);
    const midi = lerp(pitchIndexToMidi(rail.startPitchIndex), pitchIndexToMidi(rail.endPitchIndex), t);
    return midiToFreq(midi);
  }

  /**
   * Builds the oscillator graph for one rail timbre, everything feeding the
   * shared lowpass → gain chain. Pad runs two detuned saws each through a 0.5
   * trim so its summed peak matches the single-oscillator timbres; vib adds an
   * LFO into the oscillator's frequency with a depth proportional to the pitch
   * (constant musical width across the glide).
   */
  private createRailVoice(timbre: RailTimbre, freq: number, now: number): RailVoice {
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = RAIL_VOICE_LP_HZ;
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    filter.connect(gain);
    gain.connect(this.master);
    const oscs: OscillatorNode[] = [];
    let lfo: OscillatorNode | null = null;
    let lfoDepth: GainNode | null = null;
    if (timbre === "pad") {
      for (const cents of [-RAIL_PAD_DETUNE_CENTS, RAIL_PAD_DETUNE_CENTS]) {
        const osc = this.ctx.createOscillator();
        osc.type = "sawtooth";
        osc.frequency.value = freq;
        osc.detune.value = cents;
        const trim = this.ctx.createGain();
        trim.gain.value = 0.5;
        osc.connect(trim);
        trim.connect(filter);
        osc.start(now);
        oscs.push(osc);
      }
    } else {
      const osc = this.ctx.createOscillator();
      osc.type = timbre === "saw" ? "sawtooth" : timbre === "tri" ? "triangle" : "sine";
      osc.frequency.value = freq;
      osc.connect(filter);
      osc.start(now);
      oscs.push(osc);
      if (timbre === "vib") {
        lfo = this.ctx.createOscillator();
        lfo.type = "sine";
        lfo.frequency.value = RAIL_VIB_RATE_HZ;
        lfoDepth = this.ctx.createGain();
        lfoDepth.gain.value = freq * RAIL_VIB_DEPTH_RATIO;
        lfo.connect(lfoDepth);
        lfoDepth.connect(osc.frequency);
        lfo.start(now);
      }
    }
    return { oscs, lfo, lfoDepth, filter, gain, timbre, lastActive: now };
  }

  /**
   * Same offline split as killVoice: the stop is anchored to the engine clock,
   * and the disconnects only run on a live context so an offline rail voice
   * keeps the sound it already produced.
   */
  private killRailVoice(voice: RailVoice): void {
    const now = this.now();
    for (const osc of voice.oscs) {
      osc.stop(now);
    }
    voice.lfo?.stop(now);
    if (this.timeSource) {
      return;
    }
    for (const osc of voice.oscs) {
      osc.disconnect();
    }
    voice.lfo?.disconnect();
    voice.lfoDepth?.disconnect();
    voice.filter.disconnect();
    voice.gain.disconnect();
  }

  private combineGain(peak: number, impact: number, density: number): number {
    const normImpact = Math.min((impact * IMPACT_MAX) / IMPACT_REF, 1);
    const droop = lerp(1, this.densityGainFloor, density);
    return Math.min(Math.pow(peak, AUDIO_COUNT_EXP) * normImpact * AUDIO_GAIN_PER_HIT * droop, AUDIO_GAIN_MAX);
  }

  private pitchIndexToFreq(pitchIndex: number): number {
    return midiToFreq(pitchIndexToMidi(pitchIndex));
  }

  /**
   * Fades out and schedules a hard stop on a voice being cut short — a kit voice
   * replaced by the same piece firing again (mono-per-piece choke, CHOKE_FADE_S)
   * or a sample voice selected by stealing/stop-all.
   * `setTargetAtTime` with tau = fadeS/3 reaches ~95% silence at fadeS; the hard
   * stop at 5×tau lands after the signal is effectively inaudible, avoiding a
   * click. The voice is evicted from `this.voices` immediately so it is not
   * eligible for stealing; `onended` will still fire and safely no-op the
   * already-removed entry.
   */
  private chokeVoice(voice: Voice, when: number, fadeS: number = CHOKE_FADE_S): void {
    if (voice.sample) {
      this.heldVoices.get(voice.timbre)?.delete(voice);
    }
    voice.gain.gain.setTargetAtTime(0, when, fadeS / 3);
    const stopAt = when + fadeS * 5;
    for (const node of voice.nodes) {
      try {
        node.stop(stopAt);
      } catch {
        // node already stopped naturally before the choke arrived
      }
    }
    const idx = this.voices.indexOf(voice);
    if (idx >= 0) {
      this.voices.splice(idx, 1);
    }
  }

  /**
   * Plays one impact voice. With no wired envelope, sample/kit timbres keep a
   * flat gain (the recorded transient owns decay) while synth timbres use the
   * recipe AD; a wired envelope object overrides this with an impulse ADSR
   * (A→D→R) applied to every synth kind — directly as the synth amplitude, and as
   * a level multiply over the sample's own decay so the waveform is untouched.
   * Density shaping (attack blur, gain droop) is applied to synth
   * timbres only; sample timbres receive density as a no-op because transient
   * rounding and gain droop do not translate cleanly to pre-recorded audio.
   * A sustain-capable sample voice reads its pedal value at trigger time: at or
   * above the hold threshold it rings the full sample and is released early
   * only by pedal-up; below it schedules a damper — a gain fade after a hold
   * that grows exponentially from SUSTAIN_DAMP_HOLD_S (pedal 0) toward the
   * buffer's full length (pedal near 1), plus an early stop — because the asset
   * is a pedal-down recording whose full 3s ring would otherwise sound on every
   * hit and pile up in the pool. Held voices stay in `this.voices`, so
   * VOICE_CAP stealing still applies and pedal buildup cannot exceed the cap.
   */
  /**
   * Schedules an impulse ADSR on a gain node — 0→peak over attack, peak→peak·S
   * over decay, peak·S→0 over release — and returns the time it reaches silence.
   * Attack / release are floored at AUDIO_MIN_ATTACK_S so A=0 / R=0 still ramp a
   * couple of milliseconds and never click. Drives the synth fallback's amplitude
   * and multiplies onto a sample voice's flat level alike.
   */
  private scheduleImpulseAdsr(param: AudioParam, peak: number, env: ADSR, when: number): number {
    const a = Math.max(env.attackS, AUDIO_MIN_ATTACK_S);
    const d = Math.max(env.decayS, ENV_MIN_SEG_S);
    const r = Math.max(env.releaseS, AUDIO_MIN_ATTACK_S);
    const susAbs = Math.max(peak * env.sustainLevel, ENV_GAIN_FLOOR);
    param.setValueAtTime(ENV_GAIN_FLOOR, when);
    param.linearRampToValueAtTime(Math.max(peak, ENV_GAIN_FLOOR), when + a);
    param.linearRampToValueAtTime(susAbs, when + a + d);
    param.linearRampToValueAtTime(ENV_GAIN_FLOOR, when + a + d + r);
    return when + a + d + r;
  }

  private playVoice(pitchIndex: number, gainValue: number, when: number, timbre: Timbre, density: number): void {
    if (this.timeSource) {
      this.pruneEndedVoices(when);
    }
    if (this.voices.length >= VOICE_CAP) {
      this.stealVoice();
    }
    const recipe = this.recipe[timbre];
    const env = this.timbreEnvelope[timbre] ?? null;

    if (recipe.synth === "mic") {
      const bufs = this.micSource?.buffersInOrder() ?? [];
      if (bufs.length === 0) {
        return;
      }
      const pieceIdx = kitPieceIndex(pitchIndex, bufs.length);
      const buffer = bufs[pieceIdx];
      if (buffer === null) {
        return;
      }
      const trim = this.micSource?.trimsInOrder()[pieceIdx] ?? null;
      const offsetS = trim !== null ? trim.start : 0;
      const playS = trim !== null ? Math.max(0, trim.end - trim.start) : buffer.duration;
      const gain = this.ctx.createGain();
      if (env) {
        this.scheduleImpulseAdsr(gain.gain, gainValue * recipe.level, env, when);
      } else {
        gain.gain.setValueAtTime(gainValue * recipe.level, when);
      }
      gain.connect(this.timbreBus[timbre]);
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      const jitter = (Math.random() * 2 - 1) * recipe.randomDetuneCents;
      source.detune.value = jitter + (this.timbreTranspose[timbre] ?? 0) * 100;
      source.connect(gain);
      source.start(when, offsetS, playS);
      const voice: Voice = { nodes: [source], gain, timbre, sample: false, endsAt: when + playS };
      source.onended = (): void => {
        gain.disconnect();
        const idx = this.voices.indexOf(voice);
        if (idx >= 0) {
          this.voices.splice(idx, 1);
        }
      };
      this.voices.push(voice);
      return;
    }

    if (recipe.synth === "sample" || recipe.synth === "kit") {
      const midi = pitchIndexToMidi(pitchIndex);
      const pieceIdx = recipe.synth === "kit"
        ? kitPieceIndex(pitchIndex, recipe.pieces.length)
        : -1;
      const url = recipe.synth === "sample"
        ? nearestZone(recipe.zones, midi).url
        : recipe.pieces[pieceIdx];

      const workletNode = this.sampleWorkletNodes.get(timbre);
      const workletUrls = this.sampleUrlsByTimbre.get(timbre);
      if (workletNode !== undefined && workletUrls !== undefined && this.sampleWorkletReady.has(timbre)) {
        const jitter = (Math.random() * 2 - 1) * recipe.randomDetuneCents;
        const detuneCents = recipe.synth === "sample"
          ? (midi - nearestZone(recipe.zones, midi).rootMidi) * 100 + jitter
          : jitter;
        workletNode.port.postMessage({
          type: "noteOn",
          sampleId: workletUrls.indexOf(url),
          startTime: when,
          level: gainValue * recipe.level,
          detuneCents,
          mono: recipe.synth === "kit",
          sustainCapable: recipe.synth === "sample" && SUSTAIN_TIMBRES.includes(timbre),
          pedal: this.sustainValue(timbre),
          hasAdsr: env !== null,
          attackS: env ? env.attackS : 0,
          decayS: env ? env.decayS : 0,
          sustainLevel: env ? env.sustainLevel : 0,
          releaseS: env ? env.releaseS : 0,
        });
        return;
      }

      const buffer = this.sampleBuffers.get(url) ?? null;
      if (buffer === null) {
        return;
      }
      if (recipe.synth === "kit") {
        const prev = this.kitChoke.get(pieceIdx);
        if (prev !== undefined) {
          this.chokeVoice(prev, when);
        }
      }
      const gain = this.ctx.createGain();
      if (env) {
        this.scheduleImpulseAdsr(gain.gain, gainValue * recipe.level, env, when);
      } else {
        gain.gain.setValueAtTime(gainValue * recipe.level, when);
      }
      gain.connect(this.timbreBus[timbre]);
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      const jitter = (Math.random() * 2 - 1) * recipe.randomDetuneCents;
      source.detune.value = recipe.synth === "sample"
        ? (midi - nearestZone(recipe.zones, midi).rootMidi) * 100 + jitter
        : jitter;
      source.connect(gain);
      source.start(when);
      const voice: Voice = { nodes: [source], gain, timbre, sample: recipe.synth === "sample", endsAt: when + buffer.duration };
      if (recipe.synth === "kit") {
        this.kitChoke.set(pieceIdx, voice);
      } else if (SUSTAIN_TIMBRES.includes(timbre) && !env) {
        const pedal = this.sustainValue(timbre);
        if (pedal >= SUSTAIN_HOLD_THRESHOLD) {
          this.holdVoice(timbre, voice);
        } else {
          this.applyDamper(voice, pedal, when);
        }
      }
      source.onended = (): void => {
        gain.disconnect();
        const idx = this.voices.indexOf(voice);
        if (idx >= 0) {
          this.voices.splice(idx, 1);
        }
        if (recipe.synth === "kit" && this.kitChoke.get(pieceIdx) === voice) {
          this.kitChoke.delete(pieceIdx);
        }
        if (voice.sample) {
          this.heldVoices.get(voice.timbre)?.delete(voice);
        }
      };
      this.voices.push(voice);
      return;
    }

    const synthNode = this.synthWorkletNodes.get(timbre);
    if (synthNode !== undefined) {
      const base = this.pitchIndexToFreq(pitchIndex) * Math.pow(2, recipe.transpose);
      const detune = (Math.random() * 2 - 1) * this.densityDetuneCents * density;
      const detuneRatio = Math.pow(2, detune / 1200);
      const peak = gainValue * recipe.level;
      const attackS = env
        ? Math.max(env.attackS, AUDIO_MIN_ATTACK_S)
        : Math.max(recipe.attack + density * this.densityTransient * AUDIO_DENSITY_ATTACK_ADD_S, AUDIO_MIN_ATTACK_S);
      const decayS = env
        ? Math.max(env.decayS, ENV_MIN_SEG_S)
        : recipe.decay * (1 + density * this.densityTransient * AUDIO_DENSITY_DECAY_MUL);
      const sustainLevel = env ? env.sustainLevel : -1;
      const releaseS = env ? Math.max(env.releaseS, AUDIO_MIN_ATTACK_S) : 0;
      if (recipe.synth === "fm") {
        synthNode.port.postMessage({
          type: "noteOn", kind: 0, peak, attackS, decayS, sustainLevel, releaseS, startTime: when,
          freqHz: base * detuneRatio,
          modFreqHz: base * recipe.modRatio * detuneRatio,
          modDepthHz: base * recipe.modIndex * detuneRatio,
        });
      } else if (recipe.synth === "subtractive") {
        synthNode.port.postMessage({
          type: "noteOn", kind: 1, peak, attackS, decayS, sustainLevel, releaseS, startTime: when,
          freqHz: base * detuneRatio, wave: oscTypeToWave(recipe.oscType),
        });
      } else {
        const total = recipe.partials.reduce((sum: number, p: TimbrePartial): number => sum + p.level, 0);
        synthNode.port.postMessage({
          type: "noteOn", kind: 2, peak, attackS, decayS, sustainLevel, releaseS, startTime: when,
          wave: oscTypeToWave(recipe.oscType),
          partialFreqs: recipe.partials.map((p: TimbrePartial): number => base * p.ratio * detuneRatio),
          partialLevels: recipe.partials.map((p: TimbrePartial): number => p.level / total),
        });
      }
      return;
    }

    const freq = this.pitchIndexToFreq(pitchIndex);
    const effAttack = recipe.attack + density * this.densityTransient * AUDIO_DENSITY_ATTACK_ADD_S;
    const effDecay = recipe.decay * (1 + density * this.densityTransient * AUDIO_DENSITY_DECAY_MUL);
    const peak = gainValue * recipe.level;
    const gain = this.ctx.createGain();
    let end: number;
    if (env) {
      end = this.scheduleImpulseAdsr(gain.gain, peak, env, when);
    } else {
      end = when + effAttack + effDecay;
      gain.gain.setValueAtTime(0.0001, when);
      gain.gain.linearRampToValueAtTime(peak, when + effAttack);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
    }
    gain.connect(this.timbreBus[timbre]);
    const nodes = this.buildVoiceNodes(recipe, freq, gain, when, end, density);
    const voice: Voice = { nodes, gain, timbre, sample: false, endsAt: end + SYNTH_STOP_PAD_S };
    nodes[nodes.length - 1].onended = (): void => {
      gain.disconnect();
      const idx = this.voices.indexOf(voice);
      if (idx >= 0) {
        this.voices.splice(idx, 1);
      }
    };
    this.voices.push(voice);
  }

  /**
   * Builds the oscillator graph for one timbre, all feeding `gain` (which owns
   * the envelope). Additive partial levels are normalized to sum to one so the
   * combined peak equals `gainValue` regardless of how many partials are
   * configured — a struck-bar stack never sums past the single-oscillator peak,
   * keeping the limiter headroom identical to `tone`. FM drives the carrier
   * frequency through a depth gain; the carrier alone reaches `gain`, so its
   * peak also matches `tone`. The density detune is one cents value per voice
   * applied uniformly to every oscillator, so the whole note shifts together;
   * a per-oscillator random would skew the partial/carrier-modulator ratios and
   * change the timbre rather than just the pitch.
   */
  private buildVoiceNodes(recipe: SynthTimbreRecipe, baseFreq: number, gain: GainNode, when: number, end: number, density: number): AudioScheduledSourceNode[] {
    const stopAt = end + SYNTH_STOP_PAD_S;
    const freq = baseFreq * Math.pow(2, recipe.transpose);
    const detune = (Math.random() * 2 - 1) * this.densityDetuneCents * density;
    if (recipe.synth === "fm") {
      const carrier = this.ctx.createOscillator();
      carrier.type = "sine";
      carrier.frequency.value = freq;
      carrier.detune.value = detune;
      const modulator = this.ctx.createOscillator();
      modulator.type = "sine";
      modulator.frequency.value = freq * recipe.modRatio;
      modulator.detune.value = detune;
      const modDepth = this.ctx.createGain();
      modDepth.gain.value = freq * recipe.modIndex;
      modulator.connect(modDepth);
      modDepth.connect(carrier.frequency);
      carrier.connect(gain);
      modulator.start(when);
      carrier.start(when);
      modulator.stop(stopAt);
      carrier.stop(stopAt);
      return [modulator, carrier];
    }
    if (recipe.synth === "additive") {
      const total = recipe.partials.reduce((sum: number, p: TimbrePartial): number => sum + p.level, 0);
      return recipe.partials.map((p: TimbrePartial): OscillatorNode => {
        const osc = this.ctx.createOscillator();
        osc.type = recipe.oscType;
        osc.frequency.value = freq * p.ratio;
        osc.detune.value = detune;
        const partialGain = this.ctx.createGain();
        partialGain.gain.value = p.level / total;
        osc.connect(partialGain);
        partialGain.connect(gain);
        osc.start(when);
        osc.stop(stopAt);
        return osc;
      });
    }
    const osc = this.ctx.createOscillator();
    osc.type = recipe.oscType;
    osc.frequency.value = freq;
    osc.detune.value = detune;
    osc.connect(gain);
    osc.start(when);
    osc.stop(stopAt);
    return [osc];
  }

  /**
   * Frees one pool slot for a new voice: the victim is the oldest voice of the
   * timbre currently occupying the most slots. A flooding timbre cannibalizes
   * itself (it holds the majority), while a minority timbre can grow toward an
   * even share instead of being forced to choke its own newest voices — a
   * same-timbre-first policy starved minorities exactly that way. Sample
   * victims are choked (short fade) instead of hard-stopped — their long
   * recorded tails make an instant cut audible as a click, while synth victims
   * keep the hard stop because their gain carries scheduled envelope ramps that
   * a retarget would fight.
   */
  private stealVoice(): void {
    const counts = new Map<Timbre, number>();
    let majority: Timbre = this.voices[0].timbre;
    let majorityCount = 0;
    for (const voice of this.voices) {
      const count = (counts.get(voice.timbre) ?? 0) + 1;
      counts.set(voice.timbre, count);
      if (count > majorityCount) {
        majority = voice.timbre;
        majorityCount = count;
      }
    }
    const victim = this.voices.find((v: Voice): boolean => v.timbre === majority)!;
    if (victim.sample) {
      this.chokeVoice(victim, this.now());
      return;
    }
    this.killVoice(victim);
  }

  /**
   * Removes pool entries whose scheduled end has passed (offline engine only;
   * see Voice.endsAt). Held-voice and kit-choke registries are cleaned the way
   * the realtime onended handlers would.
   */
  private pruneEndedVoices(now: number): void {
    for (let i = this.voices.length - 1; i >= 0; i--) {
      const voice = this.voices[i];
      if (voice.endsAt > now) {
        continue;
      }
      this.voices.splice(i, 1);
      this.heldVoices.get(voice.timbre)?.delete(voice);
      for (const [pieceIdx, choked] of this.kitChoke) {
        if (choked === voice) {
          this.kitChoke.delete(pieceIdx);
        }
      }
    }
  }

  /**
   * The hard steal for synth victims. stop() / disconnect() carry an implicit
   * "now" that only exists on a live context, so the offline engine schedules
   * the stop at the sim clock and skips the disconnect — detaching a node
   * before startRendering would erase the victim's already-sounded past,
   * whereas realtime must disconnect to free the envelope's gain node.
   */
  private killVoice(voice: Voice): void {
    if (voice.sample) {
      this.heldVoices.get(voice.timbre)?.delete(voice);
    }
    for (const osc of voice.nodes) {
      osc.onended = null;
      osc.stop(this.now());
      if (!this.timeSource) {
        osc.disconnect();
      }
    }
    if (!this.timeSource) {
      voice.gain.disconnect();
    }
    const idx = this.voices.indexOf(voice);
    if (idx >= 0) {
      this.voices.splice(idx, 1);
    }
  }
}
